/* ==========================================================================
   NZHC header v2 behaviour.

   One controller owns every panel, so "only one thing is open" is a property
   of the header rather than something each trigger has to remember. That was
   the bug in the previous mega-panel build: two independent hover handlers,
   neither of which closed the other.
   ========================================================================== */
(function () {
  'use strict';

  var OPEN_DELAY = 90;   // hover intent — long enough to ignore a pass-through
  var CLOSE_DELAY = 220; // forgiveness when the pointer crosses a gap
  var CLICK_GRACE = 700; // a click this soon after FOCUS opened a panel confirms it, never closes it

  function NZHCHeader(root) {
    this.root = root;
    this.openTimer = null;
    this.closeTimer = null;
    this.current = null;

    this.triggers = Array.prototype.slice.call(root.querySelectorAll('[data-h2-trigger]'));
    this.panels = Array.prototype.slice.call(root.querySelectorAll('[data-h2-panel]'));

    this.bindSticky();
    this.bindPanels();
    this.bindTabs();
    this.bindCurrency();
    this.bindMobile();
    this.bindGlobal();
  }

  /* ---------------------------------------------------------------- sticky */

  // The header's section wrapper is exactly header-height, so `position:
  // sticky` has nothing to stick to and the header scrolls away. Re-pin it when
  // the reader scrolls back up, and hold the wrapper's height open so the page
  // does not jump at the moment it goes fixed.
  NZHCHeader.prototype.bindSticky = function () {
    var self = this;
    var parent = this.root.parentElement;
    var last = window.pageYOffset;
    this.pinned = false;

    var pin = function () {
      if (self.pinned) return;
      parent.style.height = self.root.offsetHeight + 'px';
      self.root.classList.add('is-pinned');
      self.pinned = true;
      headerH = self.root.offsetHeight || headerH;
    };
    var unpin = function () {
      if (!self.pinned) return;
      self.root.classList.remove('is-pinned');
      parent.style.height = '';
      self.pinned = false;
    };

    // Height is cached: reading offsetHeight inside the scroll handler forces a
    // synchronous layout on EVERY tick. Re-measure only when the viewport
    // changes or when we pin, which are the only times it can move.
    var headerH = this.root.offsetHeight;
    var ticking = false;

    var measure = function () { headerH = self.root.offsetHeight || headerH; };
    window.addEventListener('resize', measure, { passive: true });

    var onScroll = function () {
      var y = window.pageYOffset;

      // Back in the header's own territory: let it sit where it belongs.
      if (y <= headerH) { unpin(); last = y; return; }

      if (y < last - 2) {
        pin();
      } else if (y > last + 2) {
        unpin();
        // Only touch the panels if one is actually open — otherwise this wrote
        // to every panel on every downward tick for no reason.
        if (self.current) { self.clearTimers(); self.closeNow(); }
      }
      last = y;
    };

    // One pass per frame, never more.
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { onScroll(); ticking = false; });
    }, { passive: true });
  };

  /* ---------------------------------------------------------------- panels */

  NZHCHeader.prototype.panelFor = function (name) {
    for (var i = 0; i < this.panels.length; i++) {
      if (this.panels[i].getAttribute('data-h2-panel') === name) return this.panels[i];
    }
    return null;
  };

  NZHCHeader.prototype.clearTimers = function () {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
  };

  // `how` is 'hover', 'focus' or 'click' — the click handler needs to know.
  NZHCHeader.prototype.open = function (name, how) {
    var panel = this.panelFor(name);
    if (!panel) return;
    if (this.current === name) return;

    this.closeNow();
    this.closeCurrency();
    panel.classList.add('is-open');
    this.root.classList.add('is-panel-open');
    this.current = name;
    this.openedAt = Date.now();
    this.openedBy = how || 'click';
    this.sizePanel(panel, true);

    this.triggers.forEach(function (t) {
      t.setAttribute('aria-expanded', String(t.getAttribute('data-h2-trigger') === name));
    });
  };

  // The panel animates open by height, so it needs a real number to travel to.
  // Measuring beats a magic max-height: the Shop panel alone swings ~250px
  // between tabs, and a cap generous enough for the tallest one makes the
  // shortest one snap.
  // `fromClosed` matters: opening travels from 0, but a TAB SWITCH has to
  // travel from whatever height the panel is already at. Snapping to 0 first
  // would collapse the open panel and re-expand it — a visible flash on every
  // tab change.
  NZHCHeader.prototype.sizePanel = function (panel, fromClosed) {
    if (!panel) return;
    var start = fromClosed ? 0 : Math.round(panel.getBoundingClientRect().height);

    // Measure with the transition off and the cap lifted, so scrollHeight is
    // the real open height INCLUDING padding — reading it mid-transition
    // under-measures and clips the panel.
    var prev = panel.style.transition;
    panel.style.transition = 'none';
    panel.style.height = 'auto';
    var full = panel.scrollHeight;

    // Never taller than the window below the header: on a short laptop screen
    // (1366×768) the Shop panel outgrows the viewport, and because the header
    // is pinned, the bottom rows would be unreachable. Cap it and let the panel
    // itself scroll instead.
    var cap = Math.max(240, Math.floor(window.innerHeight - panel.getBoundingClientRect().top));
    var target = Math.min(full, cap);
    panel.classList.toggle('is-capped', full > cap);

    panel.style.height = start + 'px';
    void panel.offsetHeight; // commit the start height

    // Restore the transition and commit it BEFORE changing max-height again.
    // Without this second reflow the browser only ever sees one style recalc,
    // in which `transition` was still 'none' in the before-change style — so
    // no transition starts and the panel jumps to the new height.
    panel.style.transition = prev;
    void panel.offsetHeight;

    panel.style.height = target + 'px';
  };

  NZHCHeader.prototype.closeNow = function () {
    this.panels.forEach(function (p) { p.classList.remove('is-open'); p.style.height = ''; });
    this.triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
    this.root.classList.remove('is-panel-open');
    this.current = null;
  };

  NZHCHeader.prototype.close = function () {
    var self = this;
    this.clearTimers();
    this.closeTimer = setTimeout(function () {
      // A mouseleave is not proof the pointer left: it also fires when the
      // cursor crosses a gap, or jumps. `:hover` is the authoritative answer,
      // and checking it here is what stops the menu snatching itself shut
      // while you are still moving through it.
      try { if (self.root.matches(':hover')) return; } catch (e) {}
      self.closeNow();
    }, CLOSE_DELAY);
  };

  NZHCHeader.prototype.bindPanels = function () {
    var self = this;

    this.triggers.forEach(function (trigger) {
      var name = trigger.getAttribute('data-h2-trigger');

      trigger.addEventListener('mouseenter', function () {
        self.clearTimers();
        self.openTimer = setTimeout(function () { self.open(name, 'hover'); }, OPEN_DELAY);
      });

      // No mouseleave on the trigger: the pointer has to cross the bar to reach
      // the panel below, and closing on that gap makes the menu feel like it is
      // snatching itself away. The header as a whole owns the close instead.


      // Touch and keyboard both arrive here; a trigger with a panel toggles it
      // rather than navigating, so the panel is reachable without a pointer.
      trigger.addEventListener('click', function (event) {
        if (!self.panelFor(name)) return;
        event.preventDefault();
        self.clearTimers();
        // Hover (or the focus a mouse click causes) has usually already opened
        // this panel, so a plain toggle shut the menu the moment people clicked
        // "Shop". A hover-opened panel is closed by leaving the header, never by
        // a click; a focus-opened one survives the click that caused the focus.
        // Touch and keyboard (opened by click, or a later Enter) still toggle.
        if (self.current === name) {
          if (self.openedBy === 'hover') return;
          if (Date.now() - (self.openedAt || 0) < CLICK_GRACE) return;
          self.closeNow();
        } else {
          self.open(name, 'click');
        }
      });

      trigger.addEventListener('focus', function () {
        self.clearTimers();
        self.open(name, 'focus');
      });
    });

    // Resting on a top-level item that has no panel of its own (Colourclub
    // Rewards, the quiz button, the logo, the icons) is a decision to leave the
    // open panel — without this, Explore stayed open under "Colourclub Rewards"
    // because the pointer never left the header. Same hover intent as opening.
    Array.prototype.forEach.call(
      this.root.querySelectorAll('.nzhc-h2__bar .nzhc-h2__nav-item, .nzhc-h2__bar .nzhc-h2__quiz, .nzhc-h2__bar .nzhc-h2__logo, .nzhc-h2__bar .nzhc-h2__icon'),
      function (el) {
        var name = el.getAttribute('data-h2-trigger');
        if (name && self.panelFor(name)) return;
        el.addEventListener('mouseenter', function () {
          if (!self.current) return;
          self.clearTimers();
          self.openTimer = setTimeout(function () { self.closeNow(); }, OPEN_DELAY);
        });
      }
    );

    this.panels.forEach(function (panel) {
      panel.addEventListener('mouseenter', function () { self.clearTimers(); });
    });

    // One close rule: the pointer left the header. Anywhere inside it — bar,
    // gap, tab strip, panel — keeps the menu open.
    this.root.addEventListener('mouseenter', function () { self.clearTimers(); });
    this.root.addEventListener('mouseleave', function () { self.close(); });
  };

  /* ------------------------------------------------------------------ tabs */

  NZHCHeader.prototype.bindTabs = function () {
    var self = this;
    var tabs = this.root.querySelectorAll('[data-h2-tab]');

    Array.prototype.forEach.call(tabs, function (tab) {
      var activate = function () {
        var name = tab.getAttribute('data-h2-tab');
        var scope = tab.closest('[data-h2-panel]') || self.root;

        Array.prototype.forEach.call(scope.querySelectorAll('[data-h2-tab]'), function (t) {
          t.setAttribute('aria-selected', String(t === tab));
        });
        Array.prototype.forEach.call(scope.querySelectorAll('[data-h2-tabpanel]'), function (p) {
          p.classList.toggle('is-active', p.getAttribute('data-h2-tabpanel') === name);
        });

        if (scope.classList && scope.classList.contains('is-open')) self.sizePanel(scope, false);
      };

      // Tabs switch on CLICK, not hover — the top-level items open on hover,
      // but moving across the tab strip should not shuffle the panel underneath.
      tab.addEventListener('click', function (event) { event.preventDefault(); activate(); });
      // Keyboard users still get the panel as they arrive on each tab.
      tab.addEventListener('focus', activate);
    });
  };

  /* -------------------------------------------------------------- currency */

  // Opening a panel or pressing Escape shuts the currency menu. Now that the
  // menu paints above the panel, an open one would otherwise sit on top of it.
  NZHCHeader.prototype.closeCurrency = function () {
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-h2-currency]'), function (wrap) {
      var btn = wrap.querySelector('[data-h2-currency-btn]');
      var menu = wrap.querySelector('[data-h2-currency-menu]');
      if (menu) menu.setAttribute('hidden', '');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
  };

  NZHCHeader.prototype.bindCurrency = function () {
    var self = this;
    // There are two — one in the bar and one in the mobile drawer.
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-h2-currency]'), function (wrap) {
    var btn = wrap.querySelector('[data-h2-currency-btn]');
    var menu = wrap.querySelector('[data-h2-currency-menu]');
    if (!btn || !menu) return;

    btn.addEventListener('click', function (event) {
      event.stopPropagation();
      var open = menu.hasAttribute('hidden');
      // The reverse of closeCurrency() in open(): one overlay at a time.
      if (open) { self.clearTimers(); self.closeNow(); }
      if (open) menu.removeAttribute('hidden');
      else menu.setAttribute('hidden', '');
      btn.setAttribute('aria-expanded', String(open));
    });

    document.addEventListener('click', function (event) {
      if (wrap.contains(event.target)) return;
      menu.setAttribute('hidden', '');
      btn.setAttribute('aria-expanded', 'false');
    });
    });
  };

  /* ---------------------------------------------------------------- mobile */

  NZHCHeader.prototype.bindMobile = function () {
    var self = this;
    var drawer = this.root.querySelector('[data-h2-drawer]');
    if (!drawer) return;

    var stack = ['root'];

    // `dir` is 'fwd' or 'back' to slide the incoming level in; omit it to swap
    // instantly (opening/closing the drawer itself).
    var show = function (level, dir) {
      Array.prototype.forEach.call(drawer.querySelectorAll('[data-h2-level]'), function (el) {
        var on = el.getAttribute('data-h2-level') === level;
        el.classList.toggle('is-active', on);
        el.classList.remove('is-enter-fwd', 'is-enter-back');
        if (on && dir) {
          void el.offsetWidth;
          el.classList.add('is-enter-' + dir);
        }
      });
      drawer.scrollTop = 0;
    };

    Array.prototype.forEach.call(drawer.querySelectorAll('[data-h2-level]'), function (el) {
      el.addEventListener('animationend', function () {
        el.classList.remove('is-enter-fwd', 'is-enter-back');
      });
    });

    var bar = self.root.querySelector('.nzhc-h2__mbar');

    this.setDrawer = function (open) {
      if (open && bar) drawer.style.top = Math.round(bar.getBoundingClientRect().bottom) + 'px';
      drawer.classList.toggle('is-open', open);
      document.documentElement.style.overflow = open ? 'hidden' : '';
      document.documentElement.classList.toggle('nzhc-h2-drawer-open', open);
      if (!open) { stack = ['root']; show('root'); }
      Array.prototype.forEach.call(self.root.querySelectorAll('[data-h2-burger]'), function (b) {
        b.setAttribute('aria-expanded', String(open));
      });
    };

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-h2-burger]'), function (btn) {
      btn.addEventListener('click', function () {
        self.setDrawer(!drawer.classList.contains('is-open'));
      });
    });

    Array.prototype.forEach.call(drawer.querySelectorAll('[data-h2-go]'), function (btn) {
      btn.addEventListener('click', function () {
        stack.push(btn.getAttribute('data-h2-go'));
        show(stack[stack.length - 1], 'fwd');
      });
    });

    Array.prototype.forEach.call(drawer.querySelectorAll('[data-h2-back]'), function (btn) {
      btn.addEventListener('click', function () {
        if (stack.length > 1) stack.pop();
        show(stack[stack.length - 1], 'back');
      });
    });

    show('root');
  };

  /* ---------------------------------------------------------------- global */

  NZHCHeader.prototype.bindGlobal = function () {
    var self = this;

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      self.clearTimers();
      self.closeNow();
      self.closeCurrency();
      if (self.setDrawer) self.setDrawer(false);
    });

    // A click anywhere outside the header is a decision to leave it.
    document.addEventListener('click', function (event) {
      if (self.root.contains(event.target)) return;
      self.clearTimers();
      self.closeNow();
    });

    // The viewport cap in sizePanel() depends on the window height.
    window.addEventListener('resize', function () {
      if (self.current) self.sizePanel(self.panelFor(self.current), false);
    }, { passive: true });
  };

  /* ------------------------------------------------------- late anchors */

  // Menu links such as the bundle deep-links (#rbr-bundle-…) point at app
  // widgets. The anchor is in the page from the start, but the widgets ABOVE
  // it render later and push it down, so the browser's own jump lands the
  // visitor near the top. Keep the target aligned while the layout keeps
  // shifting; stop once it has been still for a moment, after 10s, or as soon
  // as the visitor scrolls, taps or types.
  function followLateAnchor() {
    var id = '';
    try { id = decodeURIComponent((window.location.hash || '').slice(1)); } catch (e) { return; }
    if (!id || !window.ResizeObserver) return;

    var done = false, settle = null, ro = null;
    var events = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    var stop = function () {
      if (done) return;
      done = true;
      clearTimeout(settle);
      if (ro) ro.disconnect();
      events.forEach(function (type) { window.removeEventListener(type, stop, true); });
    };
    events.forEach(function (type) {
      window.addEventListener(type, stop, { capture: true, passive: true });
    });

    var align = function () {
      if (done) return;
      var el = document.getElementById(id);
      if (!el) return;
      var top = el.getBoundingClientRect().top + window.pageYOffset;
      // 'instant' beats the theme's `scroll-behavior: smooth`, which would
      // otherwise animate every correction.
      window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
      // Only count down once the widget itself has rendered — an empty app
      // container is "still" long before the content above it arrives.
      clearTimeout(settle);
      if (el.offsetHeight > 50) settle = setTimeout(stop, 1500);
    };

    ro = new ResizeObserver(align);
    ro.observe(document.body);
    align();
    setTimeout(stop, 10000);
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-nzhc-header-v2]'), function (root) {
      if (root.nzhcHeader) return;
      root.nzhcHeader = new NZHCHeader(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
    document.addEventListener('DOMContentLoaded', followLateAnchor);
  } else {
    init();
    followLateAnchor();
  }

  // The theme editor re-renders the section on every setting change.
  document.addEventListener('shopify:section:load', init);
})();
